import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, importProvidersFrom, inject, Injector, LOCALE_ID } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';
import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';

import Lara from '@primeng/themes/aura';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';

import { NgxSpinnerModule } from 'ngx-spinner';
import Rollbar from 'rollbar';

import { appRoutes } from './app.routes';
import translationsES from '../public/i18n/es.json';
import { ApiApiModule, ApiConfiguration, ApiConfigurationParameters } from 'api';
import { authInterceptor } from '@auth/interceptors/auth.interceptor';
import { errorHandlerInterceptor } from '@core/intercerptors/error-handler.interceptor';
import { environment } from './environments/environment';
import { spinnerInterceptor } from '@core/intercerptors/spinner.interceptor';
import { handleChunkLoadError, ROLLBAR, rollbarFactory, RollbarErrorHandler } from '@core/services/rollbar.service';
import { HttpErrorReportThrottle } from '@core/services/http-error-report-throttle.service';
import { describeStackProbe, isStackOverflowError } from '@core/utils';
import { UpdateService } from '@core/services/update.service';
import { applyInputNumberKeyGuard } from '@core/primeng';
import { posErrorInterceptor } from '@features/pos/interceptors/pos-error.interceptor';
import { idempotencyInterceptor } from '@core/intercerptors/idempotency.interceptor';

registerLocaleData(localeEs, 'es');

// Repone el filtro de teclado que `p-inputNumber` se salta cuando el campo tiene texto seleccionado:
// sin él, un `+` (o cualquier metacarácter de regex) acaba en `new RegExp('+', 'g')` y tumba el
// campo con `SyntaxError: Nothing to repeat`. Va aquí, a nivel de módulo y una sola vez, porque
// afecta a los ~146 `p-inputNumber` del repo y una directiva habría que importarla en cada uno.
// Issue #417.
applyInputNumberKeyGuard();

/**
 * Vía de escalado ante un chunk obsoleto que la recarga normal no resolvió: una carga limpia
 * (desregistrar SW + borrar cachés → index.html y chunks frescos del CDN). Se resuelve el
 * `UpdateService` de forma PEREZOSA (solo si el fallo persiste) para no acoplarlo al arranque
 * del `ErrorHandler`/router ni arriesgar dependencias circulares. Ver Rollbar #33.
 */
function chunkEscalation(injector: Injector): () => Promise<boolean> {
  return () => injector.get(UpdateService).forceCleanReload();
}

export function apiConfigFactory(): ApiConfiguration {
  const params: ApiConfigurationParameters = {
    basePath: environment.backendBaseUrl,
  };
  return new ApiConfiguration(params);
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: LOCALE_ID, useValue: 'es' },
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
      withEnabledBlockingInitialNavigation(),
      // Red de seguridad ante fallos de NAVEGACIÓN por chunk obsoleto tras un deploy
      // (p. ej. logout → /auth/login). Estos errores NO pasan por el ErrorHandler global,
      // por eso se manejan aquí: si es un chunk obsoleto, `handleChunkLoadError` recarga una
      // sola vez (con guarda anti-bucle) para obtener la versión nueva; si no, se reporta a
      // Rollbar para conservar visibilidad de errores de navegación legítimos. Corre en
      // contexto de inyección, por eso podemos usar `inject(ROLLBAR)`.
      withNavigationErrorHandler((event) => {
        const rollbar = inject(ROLLBAR);
        const injector = inject(Injector);
        if (handleChunkLoadError(event.error, rollbar, chunkEscalation(injector))) {
          return;
        }
        // Si la navegación murió por desbordamiento de pila, el reporte llega SIN traza (WebKit no
        // la genera), así que por sí solo no distingue "hay una recursión en el código" de "este
        // motor tiene poca pila". Adjuntamos la sonda de `@core/utils` para cerrar esa pregunta con
        // un número. Solo se mide cuando ya hubo un fallo: coste cero para quien navega bien.
        // Ver BUG-IOS-01 en `docs/trackers/TESTING_BACKLOG.md`.
        const diagnostico = isStackOverflowError(event.error) ? describeStackProbe() : {};
        rollbar.error('Navigation error', {
          url: event.url,
          message: String(event.error),
          ...diagnostico,
        });
      })
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([
        authInterceptor,
        idempotencyInterceptor,
        posErrorInterceptor,
        errorHandlerInterceptor,
        spinnerInterceptor,
      ])
    ),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: { preset: Lara, options: { darkModeSelector: '.app-dark' } },
      ripple: true,
      translation: translationsES,
      // Monta los overlays (p-select, p-multiselect, p-datepicker, etc.) en el <body>
      // por defecto, para que no se recorten dentro de p-dialog / contenedores con overflow.
      // El input local `appendTo` de cada componente tiene prioridad sobre este valor global.
      overlayAppendTo: 'body',
    }),
    importProvidersFrom(ApiApiModule.forRoot(apiConfigFactory), NgxSpinnerModule.forRoot({})),
    MessageService,
    { provide: ROLLBAR, useFactory: rollbarFactory },
    {
      provide: ErrorHandler,
      // El throttle se comparte con `errorHandlerInterceptor` (singleton `providedIn: 'root'`, sin
      // dependencias, seguro de resolver aquí). Corta la amplificación de los `HttpErrorResponse`
      // que escapan por `captureUncaught` al leer el `value()` de un rxResource en error. Ver DT-08.
      useFactory: (rollbar: Rollbar, injector: Injector) =>
        new RollbarErrorHandler(rollbar, chunkEscalation(injector), injector.get(HttpErrorReportThrottle)),
      deps: [ROLLBAR, Injector],
    },
  ],
};
